// Thin client wrappers around /api/notifications.  Plain fetch + Bearer,
// matching how Settings and ShareSection talk to the API.  Callers are
// responsible for passing a valid access token (the auth context exposes
// one whenever state is `authenticated` or `offline`).

export type NotificationPayload = Record<string, unknown>;

export type Notification = {
  id: string;
  type: string;
  payload: NotificationPayload;
  readAt: string | null;
  createdAt: string;
};

export type NotificationsPage = {
  items: Notification[];
  nextCursor: string | null;
  unreadCount: number;
};

function authHeaders(token: string): HeadersInit {
  return { Authorization: `Bearer ${token}` };
}

export async function fetchNotifications(
  token: string,
  opts: { cursor?: string | null; limit?: number } = {},
): Promise<NotificationsPage> {
  const q = new URLSearchParams();
  if (opts.cursor) q.set("cursor", opts.cursor);
  if (opts.limit) q.set("limit", String(opts.limit));
  const r = await fetch(
    `/api/notifications${q.toString() ? `?${q}` : ""}`,
    { headers: authHeaders(token) },
  );
  if (!r.ok) throw new Error(`notifications ${r.status}`);
  return (await r.json()) as NotificationsPage;
}

export async function fetchUnreadCount(token: string): Promise<number> {
  const r = await fetch("/api/notifications/unread-count", {
    headers: authHeaders(token),
  });
  if (!r.ok) throw new Error(`unread-count ${r.status}`);
  const j = (await r.json()) as { unreadCount: number };
  return j.unreadCount;
}

export async function markRead(token: string, id: string): Promise<void> {
  const r = await fetch(`/api/notifications/${encodeURIComponent(id)}/read`, {
    method: "POST",
    headers: authHeaders(token),
  });
  if (!r.ok) throw new Error(`mark-read ${r.status}`);
}

export async function markAllRead(token: string): Promise<void> {
  const r = await fetch("/api/notifications/read-all", {
    method: "POST",
    headers: authHeaders(token),
  });
  if (!r.ok) throw new Error(`read-all ${r.status}`);
}
